import { useMutation, useQueryClient } from 'react-query'
import { useSelector } from 'react-redux'

import { makeSelectContentTypesByModel } from '@webapp/core/stores/global'
import { permissionRepository } from '@webapp/platform/iam/data-access/repositories/PermissionRepository'
import {
  type ReportAccessGrant,
  type ReportAccessGrantConstructor,
} from '@webapp/platform/iam/entities/ReportAccessGrant'
import { reportAccessGrantQueryConfig } from '@webapp/platform/iam/query-configs/reportAccessGrant'

const REVOKE_REPORT_ACCESS_GRANT_MUTATION_KEY = [
  'report-access-grant',
  'revoke',
]

type RevokeReportAccessGrantVariables = {
  grant: ReportAccessGrant
  secondaryCategoryId: number
}

export const useMutateRevokeReportAccessGrant = () => {
  const queryClient = useQueryClient()
  const secondaryCategoryContentType = useSelector(
    makeSelectContentTypesByModel('procurify', 'department')
  )

  const buildListQueryKey = (secondaryCategoryId: number) =>
    reportAccessGrantQueryConfig.list({
      secondaryCategoryId,
      secondaryCategoryContentTypeId: secondaryCategoryContentType.id,
    }).queryKey

  // `setQueryData` clears `isInvalidated`, which would drop a refetch still
  // owed after an invalidation-driven refetch was cancelled (Clear all or a
  // bulk command). Writes keep the mark so `onSettled` can replay it
  const setGrantsKeepingInvalidation = (
    queryKey: ReturnType<typeof buildListQueryKey>,
    updater: (
      existing: ReportAccessGrantConstructor[] | undefined
    ) => ReportAccessGrantConstructor[]
  ) => {
    const isInvalidated = queryClient.getQueryState(queryKey)?.isInvalidated

    queryClient.setQueryData<ReportAccessGrantConstructor[]>(queryKey, updater)

    if (isInvalidated) {
      queryClient.invalidateQueries(queryKey, { refetchActive: false })
    }
  }

  const { mutateAsync, isLoading } = useMutation({
    mutationKey: REVOKE_REPORT_ACCESS_GRANT_MUTATION_KEY,
    mutationFn: ({ grant }: RevokeReportAccessGrantVariables) =>
      permissionRepository.revokeReportAccessGrant(grant.id),
    // Optimistic removal (BHV-017) on the FULL key (ADR-5). In-flight
    // refetches are cancelled so they cannot resurrect the removed tag.
    onMutate: async ({ grant, secondaryCategoryId }) => {
      const queryKey = buildListQueryKey(secondaryCategoryId)
      await queryClient.cancelQueries(queryKey)

      const removedGrant = queryClient
        .getQueryData<ReportAccessGrantConstructor[]>(queryKey)
        ?.find((existingGrant) => existingGrant.id === grant.id)

      setGrantsKeepingInvalidation(queryKey, (existing = []) =>
        existing.filter((existingGrant) => existingGrant.id !== grant.id)
      )

      return { removedGrant }
    },
    // Rollback: restore the removed grant to the TOP of the list (BHV-018)
    onError: (_err, { secondaryCategoryId }, context) => {
      const removedGrant = context?.removedGrant
      if (!removedGrant) return

      setGrantsKeepingInvalidation(
        buildListQueryKey(secondaryCategoryId),
        (existing = []) => [
          removedGrant,
          ...existing.filter(
            (existingGrant) => existingGrant.id !== removedGrant.id
          ),
        ]
      )
    },
    // Replay a cancelled invalidation once the Department's last concurrent
    // revoke settles; an earlier refetch could still return a grant whose
    // DELETE is pending. A plain cancelled refetch is not replayed, keeping
    // the BHV-013/BHV-018 order
    onSettled: (_data, _err, { secondaryCategoryId }) => {
      const queryKey = buildListQueryKey(secondaryCategoryId)
      const isLastPendingRevoke =
        queryClient.isMutating({
          mutationKey: REVOKE_REPORT_ACCESS_GRANT_MUTATION_KEY,
          predicate: ({ state }) => {
            const variables: RevokeReportAccessGrantVariables | undefined =
              state.variables

            return variables?.secondaryCategoryId === secondaryCategoryId
          },
        }) <= 1

      if (
        isLastPendingRevoke &&
        queryClient.getQueryState(queryKey)?.isInvalidated
      ) {
        queryClient.invalidateQueries(queryKey)
      }
    },
  })

  return {
    revokeReportAccessGrant: mutateAsync,
    isRevoking: isLoading,
  }
}
